// src/utils/setupTenants.js
import { tenantService } from '../services/tenantService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Setup initial tenants for HealeHub multi-tenant system
 */
export const setupInitialTenants = async () => {
  try {
    console.log('🏢 Setting up initial tenants...');

    // Define the two initial clinics
    const tenants = [
      {
        name: 'HealeHub Clinic',
        slug: 'healehub-clinic',
        address: '123 Wellness Ave, Health City, HC 12345',
        email: 'admin@healehub.com',
        phone: '+44 20 1234 5678',
        address: {
          street: '123 Health Street',
          city: 'London',
          postcode: 'SW1A 1AA',
          country: 'United Kingdom'
        },
        branding: {
          primaryColor: '#2563eb', // Blue
          secondaryColor: '#1e40af',
          logo: null
        },
        settings: {
          businessHours: {
            monday: { open: '08:00', close: '18:00', closed: false },
            tuesday: { open: '08:00', close: '18:00', closed: false },
            wednesday: { open: '08:00', close: '18:00', closed: false },
            thursday: { open: '08:00', close: '18:00', closed: false },
            friday: { open: '08:00', close: '18:00', closed: false },
            saturday: { open: '09:00', close: '14:00', closed: false },
            sunday: { open: '10:00', close: '16:00', closed: true }
          },
          appointmentDuration: 60,
          currency: 'GBP',
          timezone: 'Europe/London',
          treatmentTypes: [
            { id: 'physiotherapy', name: 'Physiotherapy', duration: 60, price: 150 },
            { id: 'sports-therapy', name: 'Sports Therapy', duration: 45, price: 120 },
            { id: 'consultation', name: 'Initial Consultation', duration: 90, price: 200 },
            { id: 'follow-up', name: 'Follow-up Session', duration: 30, price: 80 }
          ],
          rooms: [
            { id: 'treatment-1', name: 'Treatment Room 1', capacity: 1 },
            { id: 'treatment-2', name: 'Treatment Room 2', capacity: 1 },
            { id: 'gym', name: 'Rehabilitation Gym', capacity: 4 }
          ]
        }
      }
    ];

    const createdTenants = [];

    for (const tenantData of tenants) {
      try {
        // Check if tenant already exists
        const existingTenants = await tenantService.getAllTenants();
        const existingTenant = existingTenants.find(t => t.slug === tenantData.slug);

        if (existingTenant) {
          console.log(`✅ Tenant "${tenantData.name}" already exists with ID: ${existingTenant.id}`);
          createdTenants.push(existingTenant);
          continue;
        }

        // Create new tenant
        const newTenant = await tenantService.createTenant(tenantData);
        console.log(`✅ Created tenant "${tenantData.name}" with ID: ${newTenant.id}`);
        createdTenants.push(newTenant);

      } catch (error) {
        console.error(`❌ Error creating tenant "${tenantData.name}":`, error);
      }
    }

    console.log(`🎉 Tenant setup complete! Created/verified ${createdTenants.length} tenants.`);
    return createdTenants;

  } catch (error) {
    console.error('❌ Error setting up initial tenants:', error);
    throw error;
  }
};

/**
 * Assign a user to a specific tenant
 */
export const assignUserToTenant = async (userId, tenantId, role = 'staff') => {
  if (!db || !userId || !tenantId) {
    throw new Error('Invalid parameters for user assignment');
  }

  try {
    console.log(`👤 Assigning user ${userId} to tenant ${tenantId} with role ${role}`);
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      tenantId: tenantId,
      role: role,
      updatedAt: new Date()
    });

    console.log(`✅ Successfully assigned user to tenant`);
    return true;
  } catch (error) {
    console.error('❌ Error assigning user to tenant:', error);
    throw error;
  }
};

/**
 * Create a super admin user (can access all tenants)
 */
export const createSuperAdmin = async (userId) => {
  if (!db || !userId) {
    throw new Error('Invalid parameters for super admin creation');
  }

  try {
    console.log(`👑 Creating super admin user ${userId}`);
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: 'super_admin',
      tenantId: null, // Super admins don't belong to a specific tenant
      updatedAt: new Date()
    });

    console.log(`✅ Successfully created super admin`);
    return true;
  } catch (error) {
    console.error('❌ Error creating super admin:', error);
    throw error;
  }
};

/**
 * Get tenant information by slug (for URL routing)
 */
export const getTenantBySlug = async (slug) => {
  try {
    const tenants = await tenantService.getAllTenants();
    return tenants.find(tenant => tenant.slug === slug) || null;
  } catch (error) {
    console.error('❌ Error getting tenant by slug:', error);
    return null;
  }
};

/**
 * Migrate existing data to a specific tenant
 */
export const migrateDataToTenant = async (tenantId, dataType = 'all') => {
  console.log(`🔄 Starting data migration to tenant ${tenantId} for ${dataType}`);
  
  // This would be used to migrate existing localStorage data to a specific tenant
  // Implementation would depend on your current data structure
  
  const collections = ['clients', 'appointments', 'staff', 'invoices', 'soapNotes', 'waitlist'];
  
  if (dataType !== 'all') {
    collections = collections.filter(col => col === dataType);
  }

  for (const collection of collections) {
    try {
      // Get existing data from localStorage
      const existingData = JSON.parse(localStorage.getItem(collection) || '[]');
      
      if (existingData.length > 0) {
        // Store in tenant-scoped localStorage key
        const tenantKey = `tenants/${tenantId}/${collection}`;
        localStorage.setItem(tenantKey, JSON.stringify(existingData));
        console.log(`✅ Migrated ${existingData.length} ${collection} records to tenant ${tenantId}`);
      }
    } catch (error) {
      console.error(`❌ Error migrating ${collection} to tenant ${tenantId}:`, error);
    }
  }

  console.log(`🎉 Data migration to tenant ${tenantId} complete!`);
};
